// Choosing the graphic plate template a story's plates play in (or one
// plate's own) — from the same gallery as Templates → Graphic Plates, with
// "Create new" for a plate made from a description.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FiX, FiPlus, FiCheck, FiLoader } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { showError } from '../FullStory/shared';
import { LoopPreview, CreateDialog, inStyle, isBusy } from './PlateTemplates';


/** The plate templates catalogue, refreshed while anything is still being made. */
export const usePlateCatalogue = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await api.plateTemplates()); } catch (e) { showError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const busy = data ? data.templates.some(isBusy) : false;
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setTimeout(load, 4000);
    return () => clearTimeout(timer);
  }, [busy, data, load]);
  return [data, load];
};

/**
 * The picker: style tabs, categories, every template playing; choosing one
 * calls onPick(id). `current` is marked; `extra` adds a footer action.
 */
export const PlateTemplatePicker = ({ title, hint, current, onPick, onClose, extra }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [data, reload] = usePlateCatalogue();
  // Created Plates first, as in the gallery — or the first tab with plates, when none are made yet.
  const [style, setStyle] = useState('created');
  const [category, setCategory] = useState('all');
  const [creating, setCreating] = useState(false);
  const [picking, setPicking] = useState(null);
  const chosenTab = useRef(false);
  useEffect(() => {
    if (!data || chosenTab.current) return;
    chosenTab.current = true;
    const first = data.styles.find((s) => data.templates.some((tp) => inStyle(tp, s.id)));
    if (first && !data.templates.some((tp) => inStyle(tp, 'created'))) setStyle(first.id);
  }, [data]);

  const ofStyle = useMemo(() => (data ? data.templates.filter((tp) => inStyle(tp, style)) : []), [data, style]);
  const shown = ofStyle.filter((tp) => category === 'all' || tp.category === category);
  const label = (c) => (lang === 'te' ? c.te : c.name);
  const usable = (tp) => (tp.created ? tp.status === 'ready' : !tp.custom || tp.status === 'ready' || tp.status === 'drawing');

  const pick = async (tp) => {
    if (!usable(tp) || picking) return;
    setPicking(tp.id);
    try { await onPick(tp.id); } finally { setPicking(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-6xl max-h-[94vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {hint && <p className="text-xs text-gray-500">{hint}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-primary text-xs py-1.5 flex items-center gap-1" onClick={() => setCreating(true)}><FiPlus size={13} /> {t('plateTemplates.create')}</button>
            <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:bg-gray-100" aria-label={t('plateTemplates.close')}><FiX size={18} /></button>
          </div>
        </div>
        {!data ? (
          <p className="p-4 text-sm text-gray-500">{t('plateTemplates.loading')}</p>
        ) : (
          <>
            <div className="px-4 pt-3 space-y-2">
              <div role="tablist" className="inline-flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium">
                {data.styles.map((s) => (
                  <button key={s.id} role="tab" aria-selected={style === s.id} onClick={() => { setStyle(s.id); setCategory('all'); }}
                    className={`px-3 py-1 rounded-md ${style === s.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                    {label(s)}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[{ id: 'all', name: t('plateTemplates.all'), te: t('plateTemplates.all') }, ...data.categories].map((c) => {
                  const count = c.id === 'all' ? ofStyle.length : ofStyle.filter((tp) => tp.category === c.id).length;
                  if (!count) return null;
                  return (
                    <button key={c.id} onClick={() => setCategory(c.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${category === c.id ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {label(c)} <span className={category === c.id ? 'text-white/70' : 'text-gray-400'}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {shown.map((tp) => (
                  <li key={tp.id}>
                    <button onClick={() => pick(tp)} disabled={!usable(tp)}
                      className={`relative w-full text-left rounded-lg border overflow-hidden ${current === tp.id ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200 hover:border-primary-300'} ${usable(tp) ? '' : 'opacity-70 cursor-wait'}`}>
                      <LoopPreview template={tp} ready={data.ready[tp.id] && (!(tp.custom || tp.created) || tp.status === 'ready')} failed={Boolean(data.failed?.[tp.id])} className="aspect-video" />
                      {current === tp.id && <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center"><FiCheck size={14} /></span>}
                      {picking === tp.id && <span className="absolute inset-0 bg-white/60 flex items-center justify-center"><FiLoader className="animate-spin text-primary-600" /></span>}
                      <span className="block px-2 py-1.5 text-xs text-gray-800 truncate">
                        {tp.te || tp.name}{tp.te && tp.name ? <span className="text-gray-400"> · {tp.name}</span> : null}
                        {tp.custom && <span className="ml-1 rounded-full bg-violet-100 text-violet-700 px-1.5">{t('plateTemplates.customBadge')}</span>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
        {extra && <div className="px-4 py-3 border-t border-gray-100 flex justify-end">{extra}</div>}
      </div>
      {creating && (
        <CreateDialog categories={data?.categories || []} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); setStyle('created'); setCategory('all'); reload(); }} />
      )}
    </div>
  );
};

/** A template's poster and name, for showing which one is in use. */
export const PlateTemplateBadge = ({ templateId, catalogue, className = '' }) => {
  const tp = catalogue?.templates.find((x) => x.id === templateId);
  return (
    <span className={`inline-flex items-center gap-2 min-w-0 ${className}`}>
      <img src={api.plateTemplateUrl(templateId, 'poster.jpg')} alt="" className="h-10 aspect-video rounded border border-gray-200 object-cover flex-shrink-0" loading="lazy" />
      <span className="truncate">{tp ? (tp.te || tp.name) : templateId}</span>
    </span>
  );
};
